import { lazy, Suspense } from 'react';

import type { OrganizationSiteV2 } from '@rtkwlf/rendall';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';

import type { FeatureConfig } from '@platform/client/features';

import { useOrganizationSiteV2 } from '@apis/rendall/queries/use-organization-site-v2';
import { SoarApiProvider } from '@apis/soar-active-response/soar-api-provider';
import { PagePlain } from '@packages/fenrir/pages/page-plain';

import i18n from './locales/i18n';
import PreferencesPage from './pages/preferences/notifications/preferences.page';
import { SitesFormType } from './pages/sites/types';

const SiteDetailPageLazy = lazy(
  () => import('./pages/sites/pages/view-site-page/site-detail.page'),
);
const ViewSiteDetailPage: React.FC = () => {
  const location = useLocation();
  const locationState = location.state as
    | { selectedSite: OrganizationSiteV2 }
    | undefined;
  const selectedSite = locationState?.selectedSite;

  const { siteUUID = '' } = useParams();
  const { data: site } = useOrganizationSiteV2(siteUUID, {
    enabled: !selectedSite,
  });

  const currentSite = selectedSite || site;

  return (
    <PagePlain title={currentSite?.name ?? ''}>
      <SiteDetailPageLazy initialFormType={SitesFormType.View} />
    </PagePlain>
  );
};

const AddSiteDetailPage: React.FC = () => {
  const { t } = useTranslation('telemetrySites');

  return (
    <PagePlain title={t('headings.newSite')}>
      <SiteDetailPageLazy initialFormType={SitesFormType.New} />
    </PagePlain>
  );
};

// Note: this feature is massive and should be broken down into smaller features
export default {
  routes: [
    {
      path: 'account',
      children: [
        {
          path: 'integrations',
          children: [
            {
              index: true,
              element: <Navigate to="active-response" replace />,
            },
            {
              path: 'active-response',
              element: (
                <SoarApiProvider>
                  <Outlet />
                </SoarApiProvider>
              ),
              children: [
                {
                  lazy: () =>
                    import(
                      './pages/integrations/integrations-tab-list-layout.page'
                    ),
                  children: [
                    {
                      index: true,
                      lazy: () =>
                        import('./pages/active-response/active-response.page'),
                    },
                  ],
                },
                {
                  path: 'new',
                  lazy: () =>
                    import(
                      './pages/active-response/active-response-new-integration.page'
                    ),
                },
                {
                  path: 'view/:instanceId',
                  lazy: () =>
                    import(
                      './pages/active-response/active-response-instance-id.page'
                    ),
                },
              ],
            },
            {
              path: 'itsm',
              children: [
                {
                  lazy: () =>
                    import(
                      './pages/integrations/integrations-tab-list-layout.page'
                    ),
                  children: [
                    {
                      index: true,
                      lazy: () =>
                        import(
                          './pages/itsm-integrations/itsm-integrations/itsm-integrations.page'
                        ),
                    },
                  ],
                },
                {
                  path: 'connect-wise',
                  children: [
                    {
                      index: true,
                      async lazy() {
                        const { OnboardedComponent } = await import(
                          './pages/itsm-integrations/connect-wise/connect-wise-integration.page'
                        );
                        return {
                          Component: OnboardedComponent,
                        };
                      },
                    },
                    {
                      path: 'new',
                      async lazy() {
                        const { Component } = await import(
                          './pages/itsm-integrations/connect-wise/connect-wise-integration.page'
                        );
                        return {
                          Component,
                        };
                      },
                    },
                  ],
                },
                {
                  path: 'service-now',
                  children: [
                    {
                      index: true,
                      async lazy() {
                        const { OnboardedComponent } = await import(
                          './pages/itsm-integrations/service-now/service-now-integration.page'
                        );
                        return {
                          Component: OnboardedComponent,
                        };
                      },
                    },
                    {
                      path: 'new',
                      async lazy() {
                        const { Component } = await import(
                          './pages/itsm-integrations/service-now/service-now-integration.page'
                        );
                        return {
                          Component,
                        };
                      },
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          path: 'federated-authentication',
          lazy: () =>
            import(
              './pages/federated-authentication/federated-authentication.page'
            ),
        },
        {
          path: 'sites',
          children: [
            {
              index: true,
              lazy: () => import('./pages/sites/sites.page'),
            },
            {
              path: 'request-deletion',
              lazy: () =>
                import(
                  './pages/sites/pages/sites-deletion-request/sites-deletion-request.page'
                ),
            },
            {
              path: 'view/:siteUUID',
              element: <ViewSiteDetailPage />,
            },
            {
              path: 'add',
              element: <AddSiteDetailPage />,
            },
          ],
        },
        {
          path: 'preferences',
          children: [
            {
              path: 'notifications',
              element: (
                <Suspense fallback={<div>Loading...</div>}>
                  <PreferencesPage />
                </Suspense>
              ),
            },
          ],
        },
      ],
    },
  ],
  navigation: () => ({
    orgProfile: {
      integrations: {
        title: i18n.t('account:integrations.pageTitle'),
        segment: '/account/integrations',
      },
      federatedAuth: {
        title: i18n.t('federated-authentication:pageTitle'),
        segment: '/account/federated-authentication',
      },
      sitesDirectory: {
        title: i18n.t('telemetrySites:sitesDirectory'),
        segment: '/account/sites',
      },
    },
  }),
} satisfies FeatureConfig;
